
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PermissionLevel } from "./types";

export const useCurrentUserPermissions = () => {
  return useQuery({
    queryKey: ["user_permissions"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) return {};
      
      const { data: userRoles, error: rolesError } = await supabase
        .from("user_roles")
        .select("role_id")
        .eq("user_id", session.user.id);
        
      if (rolesError) throw rolesError;
      
      if (!userRoles?.length) return {};
      
      const roleIds = userRoles.map(ur => ur.role_id);
      
      const { data: permissions, error: permissionsError } = await supabase
        .from("role_permissions")
        .select(`
          resource_id,
          permission,
          resource:resources(name)
        `)
        .in("role_id", roleIds);
        
      if (permissionsError) throw permissionsError;
      
      // Build a map of resource name to highest permission level
      const permissionMap: Record<string, PermissionLevel> = {};
      
      permissions?.forEach(p => {
        const resourceName = p.resource?.name;
        if (!resourceName) return;
        
        const currentPermission = permissionMap[resourceName] || 'none';
        
        // Set the highest permission level
        if (p.permission === 'edit' || 
            (p.permission === 'view' && currentPermission === 'none')) {
          permissionMap[resourceName] = p.permission;
        }
      });
      
      return permissionMap;
    },
  });
};

export const useHasPermission = (resourceName: string, requiredLevel: PermissionLevel = 'view') => {
  const { data: permissionMap, isLoading } = useCurrentUserPermissions();
  
  const userPermission = permissionMap?.[resourceName] || 'none';
  
  const hasPermission = userPermission === 'edit' || 
    (userPermission === 'view' && requiredLevel === 'view');
  
  return {
    hasPermission,
    isLoading
  };
};
